<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { ddlHelper, sessionHelper } from "@/utils";
import { SESSION } from "@/constants";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from '@/i18n'
import { LOOKUP_MODULE } from '@/state/modules/lookup'
import { PRODUCT_RECOMMENDATION_MODULE } from '@/state/modules/productRecommendation'
import { lookupMethods, productRecommendationMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

/**
 * Tabs & accordions component
 */
export default {
    page: {
        title: i18n.t("label.updateX", [i18n.t("module.recommendation")]),
    },
    components: { Layout, PageHeader, Multiselect },
    beforeRouteLeave(to, from, next) {
        if (to.name != "Product-query-listing") {
            sessionHelper.clearSearch(SESSION.PRODUCT_RECOMMENDATION_SEARCH);
        }
        next();
    },
    computed: {
        ...mapState(PRODUCT_RECOMMENDATION_MODULE, ["status"]),
        ...mapState(LOOKUP_MODULE),
        data() {
            return this.$store.state.productRecommendation.details;
        },
        productDdl() {
            return this.$store.state.lookup.productDdl;
        },
    },
    data() {
        return {
            title: i18n.t("label.updateX", [i18n.t("module.recommendation")]),
            options: [
                { value: 0, text: 'Pending' },
                { value: 1, text: 'Approved' }
            ],
            items: [
                {
                    text: i18n.t('module.dashboard'),
                    href: "/",
                },
                {
                    text: i18n.t("module.recommendation"),
                    href: "/product-recommendation",
                },
                {
                    text: i18n.t("label.updateX", [i18n.t('module.recommendation')]),
                    active: true,
                },
            ],
            submitted: false,
            permissions: sessionHelper.getPermissions(),
            updatePermission: null,
            readPermission: null,
            statusDdl: ddlHelper.getActivationStatus(true),
        };
    },
    validations: {
        data: {
            status: { required },
            productID: { required },
            crops: {
                $each: {
                    name: { required },
                    pestControls: {
                        $each: {
                            pestControl: { required },
                            rate: { required },
                        },
                    },
                }
            }
        },
    },
    async created() {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.PRODUCT_RECOMMENDATION_SEARCH);
        this.searchCriteriaProductSpecial = sessionHelper.retrieveSearch(SESSION.PRODUCT_SPECIAL_SEARCH);

        if (this.searchCriteriaProductSpecial != null && this.searchCriteriaProductSpecial != '') {
            this.isCloseWhenSuccess = this.searchCriteriaProductSpecial.isCloseWhenSuccess;
        }
        else {
            this.isCloseWhenSuccess = false;
        }

        await this.validPermissions();
        this.lookupProduct({ hasOptionAll: false });
        this.getProductRecommendationData();
    },
    methods: {
        ...productRecommendationMethods,
        ...lookupMethods,
        async validPermissions() {
            var jsonPermissions = JSON.parse(this.permissions)
            if (jsonPermissions.some(p => p.code === "RND_PR_PRU")) {
                this.updatePermission = true;
            }
            else {
                this.updatePermission = false;
            }

            if (jsonPermissions.some(p => p.code === "RND_PR_PRR")) {
                this.readPermission = true;
            }
            else {
                this.readPermission = false;
            }
        },
        addCropInput() {
            this.data.crops.push({
                name: "", pestControls: [{
                    pestControl: '', rate: 0
                }]
            });
        },
        removeCropInput(index) {
            if (index != 0) {
                this.data.crops.splice(index, 1);
            }
        },
        addPestControlInput(index) {
            this.data.crops[index].pestControls.push({ pestControl: "", rate: 0 });
        },
        removePestControlInput(cropIndex, index) {
            if (index != 0) {
                this.data.crops[cropIndex].pestControls.splice(index, 1);
            }
        },
        async getProductRecommendationData() {
            this.isLoading = true;
            if (this.readPermission == true) {
                await this.getProductRecommendation({ id: this.searchCriteria.selectedRecordId });
            }
            this.isLoading = false;
        },

        async requestUpdate() {
            this.submitted = true;
            // stop here if form is invalid
            this.$v.$touch();

            if (this.$v.data.$invalid) {
                console.log("Validation failed");

                for (const field in this.$v.data) {
                    if (this.$v.data[field].$invalid) {
                        console.log(`Validation failed for: ${field}`);
                    }
                }
                return;
            } else {
                let isCloseWhenSuccess = this.isCloseWhenSuccess;

                let data = {
                    id: this.data.uuid,
                    remark: this.data.remark,
                    productID: this.data.productID.value,
                    status: this.data.status.value,
                    crops: this.data.crops,
                };
                if (this.updatePermission == true) {
                    await this.updateProductRecommendation({ data, isCloseWhenSuccess });
                }
            }
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-body">
                        <form @submit.prevent="requestUpdate" v-if="readPermission == true">
                            <div class="row">
                                <div class="col-md-6">
                                    <b-form-group id="productID" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.product')" label-for="productID">
                                        <multiselect v-model="data.productID" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="productDdl"
                                            :searchable="true" :allow-empty="false">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.productID.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.product")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="recommendationStatus" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('module.status')" label-for="recommendationStatus">
                                        <multiselect v-model="data.status" deselect-label="Can't remove this value"
                                            track-by="value" label="text" placeholder="Select one" :options="options"
                                            :searchable="true" :allow-empty="false">
                                        </multiselect>
                                        <div v-if="submitted && !$v.data.status.required" class="invalid-feedback"
                                            style="display: block;">
                                            <span>{{ $t("message.requiredX", [$t("module.status")]) }}</span>
                                        </div>
                                    </b-form-group>
                                </div>

                                <div class="col-md-6">
                                    <b-form-group id="remark" label-cols-sm="4" label-cols-lg="4"
                                        :label="$t('label.remark')" label-for="remark">
                                        <textarea v-model="data.remark" class="form-control" name="textarea"
                                            rows="5"></textarea>
                                    </b-form-group>
                                </div>
                            </div>

                            <div class="col-12 mb-4">
                                <!-- Top row: Title + Add Crop Button -->
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h4 class="mb-0">{{ $t('module.crop') }}</h4>
                                    <button type="button" class="btn btn-success" @click="addCropInput">
                                        <i class="fas fa-plus"></i> {{ $t('label.addX', [$t('module.crop')]) }}
                                    </button>
                                </div>

                                <!-- Crop Blocks -->
                                <div v-for="(cropInput, index) in data.crops" :key="'crop_' + index" class="p-3 mb-3"
                                    style="border: 2px solid black; border-radius: 10px;">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <h5 class="mb-0">Crop #{{ index + 1 }}</h5>
                                        <button type="button" class="btn btn-danger btn-sm" v-if="index > 0"
                                            @click="removeCropInput(index)">
                                            <i class="fas fa-minus"></i>
                                        </button>
                                    </div>

                                    <!-- Crop Name -->
                                    <div class="row">
                                        <div class="col-md-5">
                                            <b-form-group :label="$t('label.name')">
                                                <b-form-input v-model.trim="cropInput.name"
                                                    :state="!$v.data.crops.$each[index].name.$invalid ? null : false">
                                                </b-form-input>
                                                <div v-if="submitted && $v.data.crops.$each[index].name.$error"
                                                    class="invalid-feedback d-block">
                                                    <span>{{ $t("message.requiredX", [$t("label.name")]) }}</span>
                                                </div>
                                            </b-form-group>
                                        </div>
                                    </div>

                                    <!-- Pest Controls -->
                                    <div class="row"
                                        v-for="(pestControlInput, pestInputIndex) in cropInput.pestControls"
                                        :key="'pest_' + index + '_' + pestInputIndex">
                                        <div class="col-md-5">
                                            <b-form-group :label="$t('label.pest')">
                                                <b-form-input v-model.trim="pestControlInput.pestControl"
                                                    :state="!$v.data.crops.$each[index].pestControls.$each[pestInputIndex].pestControl.$invalid ? null : false" />
                                                <div v-if="submitted && $v.data.crops.$each[index].pestControls.$each[pestInputIndex].pestControl.$error"
                                                    class="invalid-feedback d-block">
                                                    <span>{{ $t("message.requiredX", [$t("label.pest")]) }}</span>
                                                </div>
                                            </b-form-group>
                                        </div>

                                        <div class="col-md-5">
                                            <b-form-group :label="$t('label.rate')">
                                                <b-form-input v-model.trim="pestControlInput.rate"
                                                    :state="!$v.data.crops.$each[index].pestControls.$each[pestInputIndex].rate.$invalid ? null : false">
                                                </b-form-input>
                                                <div v-if="submitted && $v.data.crops.$each[index].pestControls.$each[pestInputIndex].rate.$error"
                                                    class="invalid-feedback d-block">
                                                    <span>{{ $t("message.invalidX", [$t("label.rate")]) }}</span>
                                                </div>
                                            </b-form-group>
                                        </div>

                                        <div class="col-md-2 d-flex align-items-center">
                                            <button type="button" class="btn btn-success mr-2"
                                                @click="addPestControlInput(index)">
                                                <i class="fas fa-plus"></i>
                                            </button>
                                            <button type="button" class="btn btn-danger" v-if="pestInputIndex > 0"
                                                @click="removePestControlInput(index, pestInputIndex)">
                                                <i class="fas fa-minus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 text-center" style="float: right">
                                <button class="btn btn-primary w-md waves-effect waves-light" style="margin-right: 10px"
                                    type="submit" :disabled="status.create || updatePermission != true">
                                    {{ $t("label.updateX", [$t("module.recommendation")]) }}
                                </button>
                            </div>
                        </form>
                        <div style="text-align: center;" v-else>
                            {{ $t('message.noPermissionMessage') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>